import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { showTelegramAlert } from '../utils/telegram';
import { applyTelegramTheme } from '../utils/telegram';

const ClaimPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [giftPrize, setGiftPrize] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);
  const [theme, setTheme] = useState('light');

  // Защита от двойного клика
  const isProcessingRef = useRef(false);

  // Получаем данные из location.state
  const { prizeData, shopInfo, telegramData, onConfirm } = location.state || {};

  useEffect(() => {
    // Применяем тему Telegram
    const themeData = applyTelegramTheme();
    setTheme(themeData.theme);

    // Сбрасываем флаг обработки при монтировании компонента
    isProcessingRef.current = false;

    // Проверяем, что все необходимые данные переданы
    if (!prizeData || !shopInfo || !telegramData || !onConfirm) {
      showTelegramAlert('❌ Ошибка: недостаточно данных для списания приза');
      navigate('/prize', {
        state: { prizeData, shopInfo, telegramData },
        replace: true
      });
      return;
    }
  }, [prizeData, shopInfo, telegramData, onConfirm, navigate]);

  const handleBackClick = () => {
    navigate('/prize', {
      state: { prizeData, shopInfo, telegramData },
      replace: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!giftPrize.trim()) return;

    // Защита от двойного клика
    if (isProcessingRef.current || isClaiming) {
      return;
    }

    isProcessingRef.current = true;

    try {
      setIsClaiming(true);
      await onConfirm(giftPrize.trim());
    } catch (error) {
      console.error('Error claiming prize:', error);
    } finally {
      isProcessingRef.current = false;
      setIsClaiming(false);
    }
  };

  if (!prizeData || !shopInfo || !telegramData) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} flex items-center justify-center animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="text-center animate-scale-in">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4 text-tg-text"></div>
          <p className="text-tg-hint text-sm">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen tg-gradient-${theme} animate-fade-in safe-area-top safe-area-bottom`}>
      <div className="max-w-sm mx-auto py-4 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBackClick}
            className="p-2 rounded-xl hover:bg-tg-secondary-bg transition-colors"
          >
            <svg className="w-5 h-5 text-tg-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-tg-text">
            🎁 Списание приза
          </h1>
          <div className="w-9"></div> {/* Spacer for centering */}
        </div>

        {/* Content */}
        <div className="animate-slide-up">
          <div className="mb-6">
            <p className="text-tg-hint text-sm mb-6 leading-relaxed">
              Укажите товар, который вы отдаете клиенту в обмен на приз:
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-tg-text mb-3">
                  Товар для выдачи
                </label>
                <textarea
                  value={giftPrize}
                  onChange={(e) => setGiftPrize(e.target.value)}
                  placeholder="Например: EPE 15000, Вкус: Малина"
                  className="tg-input resize-none"
                  rows="4"
                  autoFocus
                  disabled={isClaiming}
                />
              </div>

              <div className="bg-tg-secondary-bg rounded-xl p-4 mb-6">
                <div className="text-xs text-tg-hint mb-2 font-semibold">💡 Примеры:</div>
                <div className="text-xs text-tg-text space-y-1">
                  <div>• EPE 15000, Вкус: "Малина"</div>
                  <div>• IQOS 3 DUO, Цвет: "Синий"</div>
                  <div>• JUUL, Вкус: "Мята"</div>
                </div>
              </div>
            </form>
          </div>

          {/* Prize Info */}
          <div className="tg-card mb-6">
            <div className="flex items-center space-x-3 mb-3">
              {prizeData.prize?.imageUrl && prizeData.prize.imageUrl.startsWith('http') ? (
                <img
                  src={prizeData.prize.imageUrl}
                  alt={prizeData.prize.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-tg-secondary-bg flex items-center justify-center">
                  <svg className="w-6 h-6 text-tg-hint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-tg-text">
                  {prizeData.prize?.name}
                </h3>
                <p className="text-xs text-tg-hint">
                  {prizeData.owner?.firstName} {prizeData.owner?.lastName}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom bg-tg-bg/80 backdrop-blur-sm border-t border-tg-hint">
          <div className="max-w-sm mx-auto">
            <div className="flex gap-3">
              <button
                onClick={handleBackClick}
                disabled={isClaiming}
                className="flex-1 tg-button-secondary"
              >
                Отмена
              </button>
              <button
                onClick={handleSubmit}
                disabled={!giftPrize.trim() || isClaiming}
                className="flex-1 tg-button"
              >
                {isClaiming ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    <span>Списываю...</span>
                  </div>
                ) : (
                  'Списать приз'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Add padding to account for fixed footer */}
        <div className="pb-20"></div>
      </div>
    </div>
  );
};

export default ClaimPage;
